import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { Row, Button } from 'antd'
import * as d3 from 'd3'
import moment from 'moment'
import '../../../css/patient.scss'
import { CHART_COLORS } from '../../../util/chartPalette'
import { styleAxisTicks, AXIS_TITLE_FONT_SIZE, AXIS_TITLE_FONT_WEIGHT } from './tracpatch.chart.style'

// Per-day % of prescribed reps completed for ONE TracPatch exercise, drawn as a
// LINE chart. Each day's point is colored by whether the prescription was met:
//   yellow (#f5b301) for  < 100%
//   green  (#2e7d32) for >= 100%
// A dashed green reference line marks the 100% target. Mirrors the daily-reps
// aggregation in tracpatch.exercise.reps.chart.js (the bar version); the line
// form is what the Progress page asks for.
//
//   Daily target = requiredReps × requiredSets (first set of the day)
//   Daily actual = sum(repsCompleted) across every set of the day
//   Point %      = min(actual / target, 1.0) × 100   (display caps at 100%)
//
// The peak-angle error filter is deliberately NOT applied here. It judges a set
// by its peak flexion/extension, which is the right test for the ANGLE chart
// next door but says nothing about how many reps the patient did. Sets carrying
// 10 real reps with missing or degenerate peak angles were being discarded, so
// an exercise the patient finished in full rendered as "Data not available"
// while the exercise below it drew normally.

const YELLOW = '#f5b301'
const GREEN = CHART_COLORS.completeGreen

const TracPatchExerciseRepsLine = (props) => {
  const svgRef = useRef(null)
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)
  const [days, setDays] = useState(30)

  useEffect(() => {
    draw()
  }, [props.data, days])

  const getDailyReps = () => {
    if (!props.data || props.data.length === 0) return []

    const windowStart = moment().subtract(days - 1, 'days').startOf('day')
    const filterEnd = moment().endOf('day')

    const byDay = {}
    props.data.forEach((d) => {
      if (!d.completedAt) return
      const m = moment(d.completedAt)
      if (!m.isSameOrAfter(windowStart) || !m.isSameOrBefore(filterEnd)) return
      const key = m.format('YYYY-MM-DD')
      if (!byDay[key]) {
        byDay[key] = {
          date: m.clone().startOf('day').toDate(),
          dateLabel: m.format('MM/DD'),
          repsDone: 0,
          requiredReps: d.requiredReps || 0,
          requiredSets: d.requiredSets || 0,
        }
      }
      byDay[key].repsDone += d.repsCompleted || 0
    })

    return Object.values(byDay).sort((a, b) => a.date - b.date)
  }

  // Display % (capped at 100) and raw % (uncapped, for the tooltip / color).
  const pctFor = (d) => {
    const target = (d.requiredReps || 0) * (d.requiredSets || 0)
    if (target <= 0) return 0
    return Math.min(100, (d.repsDone / target) * 100)
  }
  const rawPctFor = (d) => {
    const target = (d.requiredReps || 0) * (d.requiredSets || 0)
    if (target <= 0) return null
    return (d.repsDone / target) * 100
  }
  const colorFor = (d) => {
    const raw = rawPctFor(d)
    if (raw == null) return '#bdbdbd'
    return raw >= 100 ? GREEN : YELLOW
  }

  const draw = () => {
    const dailyData = getDailyReps()
    if (dailyData.length === 0) return

    const margin = { top: 32, right: 20, bottom: 60, left: 50 }
    const screenWidth = containerRef.current ? containerRef.current.offsetWidth : 400
    const screenHeight = 300
    const width = screenWidth - margin.left - margin.right
    const height = screenHeight - margin.top - margin.bottom

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const g = svg
      .attr('width', screenWidth)
      .attr('height', screenHeight)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    const x = d3.scalePoint()
      .domain(dailyData.map((d) => d.dateLabel))
      .range([0, width])
      .padding(0.5)

    const y = d3.scaleLinear().domain([0, 100]).range([height, 0])

    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)')
      .call(styleAxisTicks, dailyData.length > 20 ? 9 : 11)
      .style('fill', 'black')
      .style('stroke', 'none')

    g.append('text')
      .attr('text-anchor', 'end')
      .attr('x', width / 2 + margin.left)
      .attr('y', height + margin.bottom - 5)
      .attr('stroke', 'none')
      .attr('fill', 'black')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text('Date')

    g.append('g')
      .call(d3.axisLeft(y).ticks(5).tickFormat((t) => t + '%'))
      .selectAll('text')
      .style('fill', 'black')
      .style('stroke', 'none')
      .call(styleAxisTicks, 10)

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -margin.left + 12)
      .attr('x', -height / 2)
      .attr('text-anchor', 'middle')
      .attr('fill', 'black')
      .attr('stroke', 'none')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text('% reps completed')

    // Dashed green 100% target reference line.
    g.append('line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', y(100))
      .attr('y2', y(100))
      .attr('stroke', GREEN)
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,4')
      .attr('opacity', 0.7)

    const tooltip = d3.select(tooltipRef.current)

    // Connecting line (neutral gray so the per-point yellow/green reads clearly).
    const lineGen = d3
      .line()
      .x((d) => x(d.dateLabel))
      .y((d) => y(pctFor(d)))
    g.append('path')
      .datum(dailyData)
      .attr('fill', 'none')
      .attr('stroke', '#999')
      .attr('stroke-width', 1.5)
      .attr('d', lineGen)

    // Points colored by whether the day hit 100%.
    g.selectAll('.rep-pt')
      .data(dailyData)
      .enter()
      .append('circle')
      .attr('class', 'rep-pt')
      .attr('cx', (d) => x(d.dateLabel))
      .attr('cy', (d) => y(pctFor(d)))
      .attr('r', 5)
      .attr('fill', colorFor)
      .style('cursor', 'pointer')
      .on('mouseover', function (d) {
        const target = (d.requiredReps || 0) * (d.requiredSets || 0)
        const raw = rawPctFor(d)
        tooltip
          .style('visibility', 'visible')
          .html(
            `<b>Date</b>: ${d.dateLabel}` +
            `<br/><b>Reps</b>: ${d.repsDone}${target > 0 ? '/' + target : ''}` +
            `<br/><b>%</b>: ${raw == null ? '—' : Math.round(raw) + '%'}`
          )
          .style('top', d3.select(this).attr('cy') - 50 + 'px')
          .style('left', parseInt(d3.select(this).attr('cx')) + margin.left + 10 + 'px')
      })
      .on('mouseout', () => tooltip.style('visibility', 'hidden'))

    // % labels above each point.
    const labelStep = dailyData.length > 14 ? 2 : 1
    dailyData.forEach((d, i) => {
      const fromEnd = dailyData.length - 1 - i
      if (fromEnd % labelStep !== 0) return
      const raw = rawPctFor(d)
      g.append('text')
        .attr('x', x(d.dateLabel))
        .attr('y', y(pctFor(d)) - 9)
        .attr('text-anchor', 'middle')
        .attr('fill', colorFor(d))
        .style('font-size', '10px')
        .style('font-weight', '600')
        .style('pointer-events', 'none')
        .text(raw == null ? '—' : Math.round(raw) + '%')
    })
  }

  const dailyData = getDailyReps()

  return (
    <>
      <center>
        <h4>{(props.exerciseName ? props.exerciseName + ' — ' : '') + '% reps completed'}</h4>
      </center>
      <Row justify='center' style={{ marginBottom: 8 }}>
        <Button.Group size='small'>
          <Button type={days === 7 ? 'primary' : 'default'} onClick={() => setDays(7)}>7 Days</Button>
          <Button type={days === 14 ? 'primary' : 'default'} onClick={() => setDays(14)}>14 Days</Button>
          <Button type={days === 30 ? 'primary' : 'default'} onClick={() => setDays(30)}>30 Days</Button>
        </Button.Group>
      </Row>
      {dailyData.length > 0 ? (
        <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
          <div ref={tooltipRef} className='chartToolTip' style={{ position: 'absolute', visibility: 'hidden', zIndex: 10 }} />
          <svg style={{ width: '100%', height: '300px' }} ref={svgRef} />
        </div>
      ) : (
        <Row justify='center' align='middle' style={{ height: 200 }}>
          <h2>Data not available</h2>
        </Row>
      )}
    </>
  )
}

const mapStateToProps = (state) => ({ patient: state.patient.patient })
const mapDispatchToProps = () => ({})
export default connect(mapStateToProps, mapDispatchToProps)(TracPatchExerciseRepsLine)
